import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus } from "lucide-react";
import { SectionLabel, Reveal } from "./ui";

const faqs = [
  {
    q: "What type of content do you edit?",
    a: "I primarily edit talking-head content, including short-form videos for Instagram Reels and YouTube Shorts, as well as long-form YouTube videos and VSLs. I also offer thumbnail and cover design, and can help with content ideation when needed.",
  },
  {
    q: "How long does an edit take?",
    a: "It depends on the length, complexity, and editing style. Short-form videos are typically delivered within 24 hours, while a 5–10 minute long-form video usually takes around 5 days. Timelines may vary depending on the style and references, which we can discuss before starting.",
  },
  {
    q: "Do you offer monthly packages?",
    a: "Yes, and I usually recommend them for clients who post consistently. A monthly package keeps your editing style and brand consistency consistent across your content while also giving you better value than ordering videos individually. Monthly packages also come with a discounted rate compared to ordering individual edits.",
  },
  {
    q: "How do revisions work?",
    a: "I prefer to collect revisions in one batch so the process stays fast and organized for both of us. You can request as many changes as needed within that revision round—we're talking about adjustments, not completely re-editing the entire project. Additional revision rounds are also available if needed.",
  },
  {
    q: "How much does it cost?",
    a: "Pricing depends on the project scope, editing style, content volume, and whether you choose individual edits or a monthly package. Once I understand what you need, I'll recommend the most suitable option and share the exact pricing.",
  },
  {
    q: "What do you need from me to get started?",
    a: "Once we decide to work together, I'll need your raw footage, references, brand assets, and any specific direction you have. If you don't have a detailed brief, that's completely fine—we can figure out the creative direction together.",
  },
  {
    q: "How do I get started?",
    a: "Click Start a Project, fill out the short inquiry form, and I'll get back to you with the next steps. If you'd rather talk directly, you can also message me on Instagram at @bishal.keyframes.",
  },
];

const Item = ({ item, open, onToggle, i }) => (
  <div className="border-b" style={{ borderColor: "var(--border-color)" }} data-testid={`faq-item-${i}`}>
    <button
      onClick={onToggle}
      className="group flex w-full items-center justify-between gap-6 py-7 md:py-8 text-left"
      data-testid={`faq-trigger-${i}`}
    >
      <span
        className="font-heading text-xl md:text-3xl font-medium tracking-tight transition-colors duration-300"
        style={{ color: open ? "var(--accent-color)" : "var(--text-primary)" }}
      >
        {item.q}
      </span>
      <motion.span animate={{ rotate: open ? 45 : 0 }} transition={{ duration: 0.3 }} className="shrink-0">
        <Plus size={24} style={{ color: open ? "var(--accent-color)" : "var(--text-secondary)" }} />
      </motion.span>
    </button>
    <AnimatePresence initial={false}>
      {open && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          className="overflow-hidden"
        >
          <p className="pb-8 max-w-2xl text-base md:text-lg leading-relaxed" style={{ color: "var(--text-secondary)" }}>
            {item.a}
          </p>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

export const Faq = () => {
  const [open, setOpen] = useState(0);
  return (
    <section id="faq" className="relative py-24 md:py-32 px-6 md:px-10">
      <div className="mx-auto max-w-4xl">
        <div className="mb-14 md:mb-20">
          <SectionLabel>FAQ</SectionLabel>
          <Reveal>
            <h2 className="mt-6 font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight">
              Questions I get asked a lot.
            </h2>
          </Reveal>
        </div>
        <div className="border-t" style={{ borderColor: "var(--border-color)" }}>
          {faqs.map((f, i) => (
            <Item key={i} i={i} item={f} open={open === i} onToggle={() => setOpen(open === i ? -1 : i)} />
          ))}
        </div>
      </div>
    </section>
  );
};
